import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import './PaymentSuccess.css';

/**
 * PaymentSuccess Page - Enhanced Onboarding Flow
 *
 * Shown after successful Stripe checkout payment.
 * Provides a complete onboarding experience:
 * 1. Verifies payment with backend
 * 2. Waits for Auth0 user creation (via webhook)
 * 3. Allows user to update their name
 * 4. Provides clear instructions for password setup
 * 5. Redirects to login when ready
 */
const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithRedirect, isAuthenticated, user } = useAuth0();
  const sessionId = searchParams.get('session_id');

  // State machine: verifying -> success -> onboarding -> ready -> redirecting
  const [status, setStatus] = useState('verifying');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [editedName, setEditedName] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [trialEnd, setTrialEnd] = useState(null);
  const [isUpdatingName, setIsUpdatingName] = useState(false);
  const [nameUpdateSuccess, setNameUpdateSuccess] = useState(false);
  const [userReady, setUserReady] = useState(false);
  const [pollCount, setPollCount] = useState(0);

  const API_URL = process.env.REACT_APP_API_URL || '';

  // If user is already authenticated, redirect to app
  useEffect(() => {
    if (isAuthenticated && user) {
      console.log('User already authenticated, redirecting to app...');
      navigate('/app');
    }
  }, [isAuthenticated, user, navigate]);

  // Verify payment and wait for user creation
  const verifyPayment = useCallback(async () => {
    if (!sessionId) {
      setStatus('error');
      setErrorMessage('Geen sessie-ID gevonden. Neem contact op met support.');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/checkout/success/${sessionId}`);
      const data = await response.json();

      if (data.success) {
        setEmail(data.email);
        setName(data.email?.split('@')[0] || '');
        setEditedName(data.email?.split('@')[0] || '');
        setStatus('success');

        // Start polling for user creation
        setTimeout(() => {
          setStatus('onboarding');
        }, 2000);
      } else {
        setStatus('error');
        setErrorMessage(data.message || 'Betaling nog niet voltooid');
      }
    } catch (error) {
      console.error('Payment verification error:', error);
      setStatus('error');
      setErrorMessage('Kon betalingsstatus niet verifiëren');
    }
  }, [sessionId, API_URL]);

  useEffect(() => {
    verifyPayment();
  }, [verifyPayment]);

  // Poll for user creation status
  useEffect(() => {
    if (status !== 'onboarding' || !email) return;

    const checkUserReady = async () => {
      try {
        // Check if user account is ready (created by webhook)
        const response = await fetch(`${API_URL}/api/signup/check-email/${encodeURIComponent(email)}`);
        const data = await response.json();

        if (data.exists) {
          setUserReady(true);
          setTrialEnd(data.trial_end);
        } else {
          setPollCount(prev => prev + 1);
        }
      } catch (error) {
        console.log('Checking user status...', pollCount);
        setPollCount(prev => prev + 1);
      }
    };

    // Poll every 2 seconds, max 30 attempts (1 minute)
    if (pollCount < 30 && !userReady) {
      const timer = setTimeout(checkUserReady, 2000);
      return () => clearTimeout(timer);
    } else if (pollCount >= 30 && !userReady) {
      // After 1 minute, assume user is created (webhook might be slow)
      setUserReady(true);
    }
  }, [status, email, pollCount, userReady, API_URL]);

  // Handle name update
  const handleUpdateName = async () => {
    if (!editedName.trim() || editedName === name) return;

    setIsUpdatingName(true);
    try {
      const response = await fetch(`${API_URL}/api/user/update-profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          name: editedName.trim(),
          sessionId,
        }),
      });

      if (response.ok) {
        setName(editedName.trim());
        setNameUpdateSuccess(true);
        setTimeout(() => setNameUpdateSuccess(false), 3000);
      }
    } catch (error) {
      console.error('Name update error:', error);
    } finally {
      setIsUpdatingName(false);
    }
  };

  // Handle login redirect
  const handleContinueToLogin = () => {
    setStatus('redirecting');
    loginWithRedirect({
      authorizationParams: {
        login_hint: email,
        // NO screen_hint: 'signup' - signups are disabled!
        // User account is created automatically by webhook
      },
      appState: { returnTo: '/app' },
    });
  };

  // Format trial end date
  const formatTrialEnd = (dateString) => {
    if (!dateString) return '7 dagen';
    const date = new Date(dateString);
    return date.toLocaleDateString('nl-NL', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // RENDER: Loading / Verifying
  // ─────────────────────────────────────────────────────────────────────────────
  if (status === 'verifying' || status === 'redirecting') {
    return (
      <div className="ps-page">
        <div className="ps-card">
          <div className="ps-icon-wrapper loading">
            <span className="material-icons ps-spinner">sync</span>
          </div>
          <h1 className="ps-title">
            {status === 'verifying'
              ? 'Betalingsstatus controleren...'
              : 'Je wordt doorgestuurd...'}
          </h1>
          {status === 'redirecting' && (
            <p className="ps-subtitle">
              Je wordt doorgestuurd naar de inlogpagina.
            </p>
          )}
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // RENDER: Error
  // ─────────────────────────────────────────────────────────────────────────────
  if (status === 'error') {
    return (
      <div className="ps-page">
        <div className="ps-card">
          <div className="ps-icon-wrapper error">
            <span className="material-icons">error_outline</span>
          </div>
          <h1 className="ps-title">Er is iets misgegaan</h1>
          <p className="ps-error">{errorMessage}</p>
          <div className="ps-actions">
            <Link to="/" className="ps-button">
              <span className="material-icons">arrow_back</span>
              Terug naar home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // RENDER: Success (brief celebration)
  // ─────────────────────────────────────────────────────────────────────────────
  if (status === 'success') {
    return (
      <div className="ps-page">
        <div className="ps-card">
          <div className="ps-icon-wrapper success">
            <span className="material-icons">check_circle</span>
          </div>
          <h1 className="ps-title">Betaling succesvol! 🎉</h1>
          <p className="ps-subtitle">
            Je account wordt nu aangemaakt...
          </p>
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // RENDER: Onboarding (main flow)
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className="ps-page">
      <div className="ps-card">
        <div className="ps-icon-wrapper success">
          <span className="material-icons">celebration</span>
        </div>

        <h1 className="ps-title">Welkom bij Job! 🎉</h1>

        <p className="ps-subtitle">
          Je 7-daagse gratis proefperiode is gestart. Je hebt volledige toegang
          tot alle functies.
        </p>

        {/* Trial info tile */}
        <div className="ps-info-tile">
          <div className="ps-info-icon">
            <span className="material-icons">schedule</span>
          </div>
          <div className="ps-info-content">
            <h2>Gratis Trial</h2>
            <p>
              Je trial eindigt op <strong>{formatTrialEnd(trialEnd)}</strong>.
              Je wordt pas na de trial automatisch gefactureerd.
            </p>
          </div>
        </div>

        {/* Name update section */}
        <div className="ps-steps" style={{ marginBottom: '24px' }}>
          <h3>👤 Je profiel instellen</h3>
          <div style={{ padding: '0 8px' }}>
            <div style={{ marginBottom: '16px' }}>
              <label
                style={{
                  display: 'block',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  color: '#475569',
                  marginBottom: '8px',
                  textAlign: 'left',
                }}
              >
                Je naam
              </label>
              <div style={{ display: 'flex', gap: '12px' }}>
                <input
                  type="text"
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  placeholder="Vul je volledige naam in"
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    fontSize: '1rem',
                    border: '1px solid #e2e8f0',
                    borderRadius: '10px',
                    outline: 'none',
                    transition: 'border-color 0.2s',
                  }}
                  onFocus={(e) => (e.target.style.borderColor = '#0d9488')}
                  onBlur={(e) => (e.target.style.borderColor = '#e2e8f0')}
                />
                <button
                  onClick={handleUpdateName}
                  disabled={isUpdatingName || editedName === name || !editedName.trim()}
                  style={{
                    padding: '12px 20px',
                    background:
                      editedName !== name && editedName.trim()
                        ? 'linear-gradient(135deg, #0d9488 0%, #10b981 100%)'
                        : '#e2e8f0',
                    color: editedName !== name && editedName.trim() ? 'white' : '#94a3b8',
                    border: 'none',
                    borderRadius: '10px',
                    fontSize: '0.95rem',
                    fontWeight: '600',
                    cursor:
                      editedName !== name && editedName.trim() ? 'pointer' : 'not-allowed',
                    transition: 'all 0.2s',
                  }}
                >
                  {isUpdatingName ? '...' : 'Opslaan'}
                </button>
              </div>
              {nameUpdateSuccess && (
                <p
                  style={{
                    color: '#10b981',
                    fontSize: '0.85rem',
                    marginTop: '8px',
                    textAlign: 'left',
                  }}
                >
                  ✓ Naam bijgewerkt!
                </p>
              )}
            </div>

            <div style={{ textAlign: 'left' }}>
              <label
                style={{
                  display: 'block',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  color: '#475569',
                  marginBottom: '8px',
                }}
              >
                E-mailadres
              </label>
              <p
                style={{
                  padding: '12px 16px',
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '10px',
                  margin: 0,
                  color: '#0f172a',
                  fontSize: '1rem',
                }}
              >
                {email}
              </p>
            </div>
          </div>
        </div>

        {/* Password setup instructions */}
        <div className="ps-note" style={{ marginBottom: '24px' }}>
          <span className="material-icons">mail</span>
          <p>
            <strong>Check je inbox!</strong> Je hebt een email ontvangen om je
            wachtwoord in te stellen. Check ook je spam folder.
          </p>
        </div>

        {/* Next steps */}
        <div className="ps-steps">
          <h3>Volgende stappen</h3>
          <div className="ps-steps-list">
            <div className="ps-step">
              <div
                className="ps-step-number"
                style={{
                  background: userReady
                    ? 'linear-gradient(135deg, #10b981 0%, #34d399 100%)'
                    : 'linear-gradient(135deg, #0d9488 0%, #10b981 100%)',
                }}
              >
                {userReady ? '✓' : '1'}
              </div>
              <div className="ps-step-content">
                <strong>Account aangemaakt</strong>
                <span>
                  {userReady
                    ? 'Je account is klaar!'
                    : 'Je account wordt aangemaakt...'}
                </span>
              </div>
            </div>
            <div className="ps-step">
              <div className="ps-step-number">2</div>
              <div className="ps-step-content">
                <strong>Wachtwoord instellen</strong>
                <span>Via de link in je email ({email})</span>
              </div>
            </div>
            <div className="ps-step">
              <div className="ps-step-number">3</div>
              <div className="ps-step-content">
                <strong>Inloggen en starten</strong>
                <span>Begin met het maken van vacatures met Job</span>
              </div>
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="ps-actions" style={{ marginTop: '28px' }}>
          <button
            onClick={handleContinueToLogin}
            className="ps-button"
            disabled={!userReady}
            style={{
              opacity: userReady ? 1 : 0.6,
              cursor: userReady ? 'pointer' : 'wait',
            }}
          >
            <span className="material-icons">login</span>
            {userReady ? 'Ga naar inloggen' : 'Account wordt aangemaakt...'}
          </button>
        </div>

        {!userReady && (
          <p
            style={{
              fontSize: '0.85rem',
              color: '#64748b',
              marginTop: '12px',
            }}
          >
            Even geduld, dit duurt meestal minder dan 30 seconden...
          </p>
        )}

        {/* Support */}
        <div className="ps-support" style={{ marginTop: '28px' }}>
          <p>
            Hulp nodig? Neem contact op met{' '}
            <a href="mailto:support@humanaize.nl">support@humanaize.nl</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
